
import Vue from "vue";
import permission from "@/common/mixins/permission";
import pagination from "@/common/mixins/pagination";
import dialog from "@/common/mixins/dialog";
import TableCustom from "@/components/TableCustom.vue";
import Container from "@/components/Container.vue";
import LoadingGraph from "@/components/LoadingGraph.vue";
import SearchIcon from "@/components/icons/SearchIcon.vue";

import MemberInviteModal from "../components/MemberInviteModal.vue";
import MemberEditInviteModal from "../components/MemberEditInviteModal.vue";
import { MemberInviteForm } from "../models/MemberInviteForm";
import { MemberEditInviteForm } from "../models/MemberEditInviteForm";
import { Member } from "../models/Member";
import DeleteIcon from "@/components/icons/DeleteIcon.vue";

interface InviteMemberParams {
  email: string;
  role: string;
}

export default Vue.extend({
  name: "CompanyMemberPage",
  mixins: [permission, pagination, dialog],
  components: {
    MemberInviteModal,
    TableCustom,
    Container,
    LoadingGraph,
    DeleteIcon,
    MemberEditInviteModal,
    SearchIcon,
  },
  data() {
    const memberHeaders = [
      { text: "", value: "profileImageUrl", sortable: false },
      { text: "Name", value: "name", width: "30%" },
      { text: "Email", value: "email", width: "30%" },
      { text: "Role", value: "role", align: "center" },
      { text: "Status", value: "status", align: "center" },
      { text: "", value: "action", sortable: false },
    ];

    const memberInviteForm: MemberInviteForm = {
      isShowModal: false,
      isLoading: false,
      email: "",
      role: "",
    };

    const memberEditInviteForm: MemberEditInviteForm = {
      isLoading: false,
      role: "",
      email: "",
    };

    return {
      isLoading: false,
      memberHeaders,
      members: [] as Member[],
      memberInviteForm,
      memberEditInviteForm,
      searchQuery: "",
      selectedRole: null as string | null,
      selectedStatus: null as string | null,
      roleOptions: ["owner", "owner_rvs", "member", "supmem"],
      statusOptions: ["active", "pending", "inactive"],
      currentPage: 1,
      pageSize: 10,
      totalItems: 0,
      totalPages: 0,
      debounce: 0 as any,
      pageSizeOptions: [
        { label: "5", value: 5 },
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
      ],
    };
  },
  async created() {
    const role = this.$store.getters.user?.role?.toUpperCase();
    if (!["ADMIN", "OWNER_RVS"].includes(role)) {
      this.$router.replace({ name: "CompaniesPage" });
      return;
    }
    await this.getMembers();
  },
  computed: {
    companyId() {
      return this.$route.params.id;
    },
    companyTitle(): string {
      return (this.$route.params as any).title || this.$store.getters.company?.name || "";
    },
    isAdminUser(): boolean {
      return this.$store.getters.user?.role?.toUpperCase() === "ADMIN";
    },
    hasInviteMemberToCompanyPermission() {
      const role = this.$store.getters.user?.role?.toUpperCase();
      return role === "ADMIN";
    },
    hasRemoveMemberFromCompanyPermission() {
      const role = this.$store.getters.user?.role?.toUpperCase();
      return ["ADMIN", "OWNER_RVS"].includes(role);
    },
  },
  methods: {
    async getMembers(silent = false) {
      try {
        if (!silent) this.isLoading = true;
        const response = await (this as any).$dep.manageMemberService.getCompanyMembers(this.companyId, {
          search: this.searchQuery || undefined,
          role: this.selectedRole || undefined,
          status: this.selectedStatus || undefined,
          page: this.currentPage,
          size: this.pageSize,
        });
        this.members = response.items || [];
        this.totalItems = response.total || 0;
        this.totalPages = this.totalItems > 0 ? Math.ceil(this.totalItems / this.pageSize) : 0;
      } catch {
        this.members = [];
        this.totalItems = 0;
        this.totalPages = 0;
      } finally {
        if (!silent) this.isLoading = false;
      }
    },
    onRoleFilterChange() {
      this.currentPage = 1;
      this.getMembers();
    },
    onStatusFilterChange() {
      this.currentPage = 1;
      this.getMembers();
    },
    onPageChange() {
      this.getMembers(true);
    },
    onPageSizeChange() {
      this.currentPage = 1;
      this.getMembers();
    },
    closeMemberInviteModal() {
      this.memberInviteForm = {
        ...this.memberInviteForm,
        isShowModal: false,
        email: "",
        role: "",
      };
    },
    closeMemberEditInviteModal() {
      this.memberEditInviteForm = {
        ...this.memberEditInviteForm,
        isShowModal: false,
        member: undefined,
      };
    },
    async inviteMemberInviteModal(params: InviteMemberParams) {
      try {
        this.isLoading = true;
        this.memberInviteForm = { ...params, isLoading: true };
        await (this as any).$dep.memberUseCase.inviteMember(this.companyId, this.memberInviteForm);

        this.closeMemberInviteModal();
        this.$store.dispatch("openAlertMessage", {
          open: true,
          message: "Invite member successfully",
          type: "success",
        });
        await this.getMembers();
      } catch (error: any) {
        (this as any).alertError("Error", error.response?.data?.detail || "Can not Create Admin!");
      } finally {
        this.memberInviteForm = { ...params, isLoading: false };
        this.isLoading = false;
      }
    },
    async editMemberEditInviteModal(params: any) {
      try {
        this.isLoading = true;
        this.memberEditInviteForm = { ...params, isLoading: true };

        await (this as any).$dep.memberUseCase.updateMemberProfile(this.memberEditInviteForm);

        this.closeMemberEditInviteModal();
        this.$store.dispatch("openAlertMessage", {
          open: true,
          message: "Edit member successfully",
          type: "success",
        });
        await this.getMembers();
      } catch (error) {
        this.$store.dispatch("openAlertMessage", {
          open: true,
          message: error && (error as any)?.detail ? (error as any).detail : "error: edit member failed",
          type: "error",
        });
      } finally {
        this.isLoading = false;
        this.memberEditInviteForm = { ...this.memberEditInviteForm, isLoading: false };
      }
    },
    async resendInviteMember(email: string) {
      try {
        await (this as any).$dep.memberUseCase.resendInviteMember(email);
        await (this as any).alertSuccess("Success", `resend invite completed`);
      } catch (error: any) {
        (this as any).alertError("Error", error.response?.data?.detail || "Can not Resend Invite Member!");
      }
    },
    async updateMember(memberId: number) {
      const member = this.members.find((e: any) => e.id === memberId);
      this.memberEditInviteForm = {
        ...this.memberEditInviteForm,
        isShowModal: true,
        member,
        role: (member as any)?.role || "",
      };
    },
    async restoreMember(data: any) {
      const { name, email } = data;
      const isComfirmed = await (this as any).alertBasic(
        name,
        `<p>Are you sure you want to active this member?</p>`,
        "question",
        "Confirm"
      );

      if (isComfirmed) {
        try {
          this.isLoading = true;
          this.members = [];

          await (this as any).$dep.memberUseCase.activeMember(email);
          await (this as any).alertSuccess("Success", `"${email}" has been active`);
        } catch (error: any) {
          const msgError = error.response?.data?.detail || "Can not Active Member!";
          (this as any).alertError("Error", msgError);
        } finally {
          this.isLoading = false;
          await this.getMembers();
        }
      }
    },
    async deleteMember(email: string) {
      const isConfirmed = await (this as any).alertConfirm(
        "ลบ Member",
        `คุณต้องการลบ ${email} ออกจากระบบใช่หรือไม่? ผู้ใช้ที่ถูกลบจะไม่สามารถเข้าถึงระบบในทุก company ได้อีกต่อไป`
      );
      if (isConfirmed) {
        try {
          this.isLoading = true;
          this.members = [];

          await (this as any).$dep.memberUseCase.deleteMember(email);
          await (this as any).alertSuccess("ลบ Member สำเร็จ", ``);
        } catch (error) {
          process.env.NODE_ENV === "production"
            ? (this as any).alertError("Error", "ไม่สามารถลบ Member ได้")
            : (this as any).alertError("Error", error);
        } finally {
          this.isLoading = false;
          await this.getMembers();
        }
      }
    },
    async removeFromCompany(data: any) {
      const { id, name } = data;
      const isConfirmed = await (this as any).alertConfirm(
        "ลบจาก Company นี้",
        `คุณต้องการลบ ${name} จาก Company นี้ใช่หรือไม่? ผู้ใช้ที่ถูกลบจะยังคงสามารถเข้าถึงระบบใน company อื่นๆ ที่เขาเป็นสมาชิกอยู่ได้`
      );
      if (isConfirmed) {
        try {
          this.isLoading = true;
          await (this as any).$dep.manageMemberService.removeMemberFromCompany(this.companyId, id);
          await (this as any).alertSuccess("Success", `${name} has been removed from this company`);
        } catch (error: any) {
          (this as any).alertError("Error", error.response?.data?.detail || "Cannot remove member from company");
        } finally {
          this.isLoading = false;
          await this.getMembers();
        }
      }
    },
    isOnPermission(status: string, type: string) {
      if (type === "ACTIVE") return ["inactive"].includes(status);
      if (type === "DELETE") return ["active", "pending"].includes(status);
      if (type === "RESEND_INVITE") return ["pending"].includes(status);
      if (type === "SWITCH_ROLE") return ["active"].includes(status);
      if (type === "EDIT_ROLE") return ["active"].includes(status);
      if (type === "REMOVE_FROM_COMPANY") return ["active", "pending"].includes(status);
      return false;
    },
    async memberSwitchRole(data: any) {
      const { id, email, role } = data;
      const isConfirmed = await (this as any).alertConfirm(
        "Change Role",
        `คุณต้องการเปลี่ยน Role เป็น ${role === "member" ? "Owner" : "Member"} ใช่หรือไม่ ?`
      );
      if (isConfirmed) {
        try {
          this.isLoading = true;
          this.members = [];

          await (this as any).$dep.memberUseCase.memberSwitchRole(id);
          await (this as any).alertSuccess("Success", `"${email}" has been switch role!`);
        } catch (error: any) {
          const msgError = error.response?.data?.detail || "Can not Switch Role!";
          (this as any).alertError("Error", msgError);
        } finally {
          this.isLoading = false;
          await this.getMembers();
        }
      }
    },
    async onOpenEditMemberModal(memberId: number) {
      await this.updateMember(memberId);
      (this as any).$store.dispatch("openEditMemberModal", {
        open: true,
        type: "CREATE",
        param: null,
      });
    },
  },
  watch: {
    searchQuery() {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        this.currentPage = 1;
        this.getMembers();
      }, 600);
    },
  },
});
